import { NgModule } from '@angular/core';
import { NoPreloading, RouterModule, Routes } from '@angular/router';
import { PageDataBuilder } from '@psei/ng/api';
import { DirtyCheckGuard } from 'projects/ppb-shared-library/src/lib/services/check-form-dirty-guard.service';
import { environment } from '../environments/environment';
// tslint:disable-next-line:max-line-length
import { PpbAccountActivationComponent } from './ppb/ppb-account-activation/account-activation/ppb-account-activation.component';
import { PpbAccountRegistrationComponent } from './ppb/ppb-account-registration/ppb-account-registration.component';
import { AccountResolverService } from './ppb/ppb-account/account-resolver.service';
import { PpbAccountDataComponent } from './ppb/ppb-account/ppb-account-data/ppb-account-data.component';
import { DashboardResolverService } from './ppb/ppb-dashboard/dashboard-resolver.service';
import { PpbDashboardComponent } from './ppb/ppb-dashboard/ppb-dashboard.component';
import { PpbLoginComponent } from './ppb/ppb-login/ppb-login.component';
import { PpbPasswordResetComponent } from './ppb/ppb-password-reset/ppb-password-reset.component';
import { PpbPasswordSetComponent } from './ppb/ppb-password-set/ppb-password-set.component';
import { PpbRegistrationCodesComponent } from './ppb/ppb-registration-codes/ppb-registration-codes.component';
import { PpbRootComponent } from './ppb/ppb-root/ppb-root.component';
import { UserElementalRole } from './ppb/shared/models/user-elemental-role';
import { ConfigurationResolver } from './ppb/shared/services/configuration.resolver';
import { AuthGuardService } from './shared/services/guards/auth-guard.service';
import { BrowserCompatibilityGuard } from './shared/services/guards/browser-compatibility.guard';
import { HasUserElementalRoleGuard } from './shared/services/guards/has-user-elemental-roles.guard';
import { BrowserNotSupportedComponent } from './technical/browser-not-supported/browser-not-supported.component';
import { E404Component } from './technical/e404/e404.component';

const routes: Routes = [
  {
    path: 'login',
    canActivate: [BrowserCompatibilityGuard],
    component: PpbLoginComponent,
    resolve: { configuration: ConfigurationResolver }
  },
  {
    path: 'account-activation',
    canActivate: [BrowserCompatibilityGuard],
    component: PpbAccountActivationComponent,
    resolve: { configuration: ConfigurationResolver }
  },
  {
    path: 'password-reset',
    canActivate: [BrowserCompatibilityGuard],
    component: PpbPasswordResetComponent
  },
  {
    path: 'password-set/:uuid',
    canActivate: [BrowserCompatibilityGuard],
    component: PpbPasswordSetComponent
  },
  {
    path: 'terms',
    canActivate: [BrowserCompatibilityGuard],
    loadChildren: () => import('./ppb/ppb-terms/ppb-terms.module')
      .then(m => m.PpbTermsModule)
  },
  {
    path: 'account-registration',
    component: PpbAccountRegistrationComponent,
    canActivate: [BrowserCompatibilityGuard],
    resolve: { configuration: ConfigurationResolver }
  },
  {
    path: 'invitation/:code',
    canActivate: [BrowserCompatibilityGuard],
    loadChildren: () => import('./ppb/ppb-invitation/ppb-invitation.module')
      .then(m => m.PpbInvitationModule),
  },
  {
    path: 'email-change-confirmation/:updateId',
    canActivate: [BrowserCompatibilityGuard],
    loadChildren: () => import('./ppb/ppb-email-change-confirmation/ppb-email-change-confirmation.module')
      .then(m => m.PpbEmailChangeConfirmationModule),
  },
  {
    path: '',
    component: PpbRootComponent,
    canActivate: [AuthGuardService, BrowserCompatibilityGuard],
    canActivateChild: [AuthGuardService, BrowserCompatibilityGuard],
    data: {
      afterLogin: true,
    },
    children: [
      {
        path: '', redirectTo: '/dashboard', pathMatch: 'full',
        data: {
          afterLogin: true,
        }
      },
      {
        path: 'dashboard',
        component: PpbDashboardComponent,
        canActivate: [HasUserElementalRoleGuard],
        resolve: {
          partnersArray: DashboardResolverService
        },
        data: {
          menuItemId: 'dashboard',
          requiredElementalRole: UserElementalRole.GEN_DASHBOARD,
          ...PageDataBuilder.build()
            .setTitle('Dashboard')
            .setIcon('dashboard')
            .setShowInMenu(true)
            .setShowSubMenu(true)
            .setShowBreadcrumb(true)
            .getData()
        },
      },
      {
        path: 'account',
        component: PpbAccountDataComponent,
        canActivate: [AuthGuardService, BrowserCompatibilityGuard],
        canDeactivate: [DirtyCheckGuard],
        resolve: {
          account: AccountResolverService
        },
        data: {
          ...PageDataBuilder.build()
            .setTitle($localize`:@@account.account_navigation:Moje konto`)
            .setIcon('user')
            .getData(),
          requiredElementalRole: UserElementalRole.ACC_UPDATE
        }
      },
      {
        path: 'users',
        canActivate: [HasUserElementalRoleGuard],
        loadChildren: () => import('./ppb/ppb-users/ppb-users.module')
          .then(m => m.PpbUsersModule),
        data: {
          ...PageDataBuilder.build()
            .setTitle($localize`:@@users.users_navigation:Użytkownicy`)
            .setIcon('user')
            .getData(),
          requiredElementalRole: UserElementalRole.ACC_ADM_PREVIEW
        }
      },
      {
        path: 'proposals',
        canActivate: [HasUserElementalRoleGuard],
        loadChildren: () => import('./ppb/ppb-proposals/ppb-proposals.module')
          .then(m => m.PpbProposalsModule),
        data: {
          ...PageDataBuilder.build()
            .setTitle($localize`:@@proposals.proposals_navigation:Wnioski`)
            .setIcon('filestack')
            .getData(),
          requiredElementalRole: UserElementalRole.PRO_PB_PREVIEW
        }
      },
      {
        path: 'registration-codes',
        component: PpbRegistrationCodesComponent,
        canActivate: [HasUserElementalRoleGuard],
        data: {
          ...PageDataBuilder.build()
            .setTitle($localize`:@@registration-codes.registration_code:Kod rejestracyjny`)
            .setIcon('key')
            .getData(),
          requiredElementalRole: UserElementalRole.REG_CODE_GENERATE
        }
      },
    ],
  },
  {
    path: 'browser-not-supported',
    component: BrowserNotSupportedComponent,
    resolve: { configuration: ConfigurationResolver }
  },
  {
    path: 'e404', component: E404Component, canActivate: [BrowserCompatibilityGuard],
  },
  {
    path: '**', redirectTo: '/e404', pathMatch: 'full'
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes,
    {
      scrollPositionRestoration: 'enabled',
      enableTracing: !environment.production,
      preloadingStrategy: NoPreloading
    })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
